// A tab left open across a deploy (2026-09-28, "I lost the ability to show the Customer Detail
// page" in a client demo).
//
// Every page is a lazy chunk named by a content hash, and the tab's app.js holds the map of those
// names from the build it was loaded with. A deploy replaces the files (rsync --delete on TEST,
// a new container on App Platform), and a missing chunk URL is answered with index.html and a
// 200 (Apache's FallbackResource; `serve -s`). webpack then rejects the route's import with a
// ChunkLoadError, vue-router aborts the navigation, and the click does nothing - for every page
// whose chunk changed in the deploy and that the tab had not opened yet.
//
// When that happens, load the page the user asked for as a full page load: it fetches the current
// index.html and bundle, and the page opens. At most once per page per RELOAD_WINDOW_MS, so a
// chunk that is broken in the current build does not reload for ever.

export const RELOAD_KEY = 'mbp-stale-bundle-reload'
export const RELOAD_WINDOW_MS = 60 * 1000

// webpack 4: JS chunks reject with name 'ChunkLoadError' ("Loading chunk X failed."),
// mini-css-extract-plugin with code 'CSS_CHUNK_LOAD_FAILED' ("Loading CSS chunk X failed.").
export function isChunkLoadError(error) {
  if (!error || typeof error !== 'object') return false
  if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') return true
  return /Loading (CSS )?chunk \S+ failed/.test(String(error.message || ''))
}

function readGuard(storage) {
  try {
    const raw = storage && storage.getItem(RELOAD_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

function writeGuard(storage, value) {
  try {
    if (storage) storage.setItem(RELOAD_KEY, JSON.stringify(value))
  } catch (e) {
    // No session storage (private window, blocked site data): reload without the guard.
  }
}

function defaultStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch (e) {
    return null
  }
}

export function installStaleBundleReload(router, {
  location = typeof window !== 'undefined' ? window.location : null,
  storage = defaultStorage(),
  now = () => Date.now()
} = {}) {
  // onError is not told where the navigation was going, so note each target as it starts.
  let target = null
  router.beforeEach((to, from, next) => {
    target = to
    next()
  })
  // A navigation that completed means the user is on a page that works. A chunk failure that
  // arrives after that belongs to an earlier click they have moved on from, so leave them there
  // rather than reload the page they are using (review finding, 2026-09-28).
  router.afterEach(() => {
    target = null
  })

  router.onError(error => {
    if (!isChunkLoadError(error) || !target || !location) return
    const href = router.resolve(target.fullPath).href
    const guard = readGuard(storage)
    if (guard && guard.href === href && now() - guard.at < RELOAD_WINDOW_MS) return
    writeGuard(storage, { href, at: now() })
    location.assign(href)
  })
}
